import React from "react";
import { Badge, Button, Callout, Card, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { EventRunSheet, ID, RunSheetItem } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

const STATUS_TONE: Record<RunSheetItem["status"], "neutral" | "success" | "warning" | "error"> = {
  planned: "neutral",
  in_progress: "warning",
  done: "success",
  delayed: "error",
  skipped: "neutral"
};

export function RunSheetsScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [sheets, setSheets] = React.useState<EventRunSheet[] | null>(null);
  const [sheetsLoading, setSheetsLoading] = React.useState(false);
  const [sheetsError, setSheetsError] = React.useState<string | null>(null);
  const [addingItemFor, setAddingItemFor] = React.useState<EventRunSheet | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<ID | null>(null);

  const load = React.useCallback(async () => {
    setSheetsLoading(true);
    setSheetsError(null);
    try {
      setSheets(await service.runsheets.list());
    } catch (err) {
      setSheetsError(errorMessage(err));
    } finally {
      setSheetsLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading run sheets" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const setStatus = async (item: RunSheetItem, status: RunSheetItem["status"]) => {
    setActionError(null);
    setBusyId(item.id);
    try {
      await service.runsheets.updateItem(item.id, { status });
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (item: RunSheetItem) => {
    setActionError(null);
    setBusyId(item.id);
    try {
      await service.runsheets.deleteItem(item.id);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const move = async (sheet: EventRunSheet, index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sheet.items.length) return;
    const ids = sheet.items.map((i) => i.id);
    [ids[index], ids[targetIndex]] = [ids[targetIndex], ids[index]];
    setActionError(null);
    try {
      await service.runsheets.reorder(sheet.eventId, ids);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Plan" title="Run sheets" meta={[{ icon: "list-checks", label: "Whole wedding overview" }]} />
      <div className="pob-pad">
        {sheetsLoading && !sheets && <LoadingState label="Loading run sheets" />}
        {sheetsError && <ErrorState message={sheetsError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {sheets && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            {sheets.map((sheet) => (
              <Card key={sheet.eventId} eyebrow={sheet.eventName} title="Run sheet" action={<Button size="sm" onClick={() => setAddingItemFor(sheet)}>Add item</Button>}>
                {sheet.items.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No run sheet items yet.</span>}
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                  {sheet.items.map((item, index) => (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-2) 0", borderBottom: "1px solid var(--divider)" }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <button type="button" disabled={index === 0} onClick={() => move(sheet, index, -1)} style={{ border: 0, background: "transparent", cursor: index === 0 ? "default" : "pointer", opacity: index === 0 ? 0.3 : 1 }}>▲</button>
                        <button type="button" disabled={index === sheet.items.length - 1} onClick={() => move(sheet, index, 1)} style={{ border: 0, background: "transparent", cursor: index === sheet.items.length - 1 ? "default" : "pointer", opacity: index === sheet.items.length - 1 ? 0.3 : 1 }}>▼</button>
                      </div>
                      <span style={{ font: "var(--type-label)", width: 60, color: "var(--text-secondary)" }}>{item.time || "—"}</span>
                      <div style={{ flex: 1 }}>
                        <span style={{ font: "var(--type-label)", display: "block" }}>{item.activity}</span>
                        <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>
                          {[item.ownerLabel, item.vendorName, item.venueName].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                      <Select
                        size="sm"
                        value={item.status}
                        onChange={(e) => setStatus(item, e.target.value as RunSheetItem["status"])}
                        options={[{ value: "planned", label: "Planned" }, { value: "in_progress", label: "In progress" }, { value: "done", label: "Done" }, { value: "delayed", label: "Delayed" }, { value: "skipped", label: "Skipped" }]}
                        style={{ width: 140 }}
                      />
                      <Badge tone={STATUS_TONE[item.status]}>{item.status.replace(/_/g, " ")}</Badge>
                      <Button size="sm" variant="danger" disabled={busyId === item.id} onClick={() => remove(item)}>Remove</Button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {addingItemFor && (
        <AddItemDialog sheet={addingItemFor} onClose={() => setAddingItemFor(null)} onSaved={async () => { setAddingItemFor(null); await load(); }} />
      )}
    </div>
  );
}

function AddItemDialog({ sheet, onClose, onSaved }: { sheet: EventRunSheet; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [time, setTime] = React.useState("");
  const [activity, setActivity] = React.useState("");
  const [ownerLabel, setOwnerLabel] = React.useState("");
  const [vendorId, setVendorId] = React.useState("");
  const [venueId, setVenueId] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!activity.trim()) { setFormError("Give this item an activity name."); return; }
    try {
      await service.runsheets.createItem(sheet.eventId, { time: time || undefined, activity: activity.trim(), ownerLabel: ownerLabel || undefined, vendorId: vendorId || undefined, venueId: venueId || undefined, notes: notes || undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title={`Add run sheet item — ${sheet.eventName}`} onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Time"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
        <Field label="Activity"><Input value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="Guests arrive" /></Field>
        <Field label="Owner / responsible person"><Input value={ownerLabel} onChange={(e) => setOwnerLabel(e.target.value)} /></Field>
        <Field label="Vendor (optional)"><Select value={vendorId} placeholder="None" onChange={(e) => setVendorId(e.target.value)} options={workspace!.vendors.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Venue (optional)"><Select value={venueId} placeholder="None" onChange={(e) => setVenueId(e.target.value)} options={workspace!.venues.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
